/**
 * Structured data (JSON-LD) and document head for the vendor market list route.
 * Breadcrumb labels in JSON-LD use English (common practice); UI meta strings use i18n.
 */

import { env } from '@web/src/lib/env.client'
import i18n from '@web/src/lib/intl/i18n'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'

/** Matches `validateSearch` default — filters and JSON-LD treat this as “no radius filter”. */
const DEFAULT_VENDOR_LIST_DISTANCE = 250

const LIST_PATH = '/vendor-market/list'

export type VendorListSeoItem = {
  name: string
  path: string
  imageUrl?: string | null
}

export type VendorListSeoLoaderData = {
  seoList: {
    items: VendorListSeoItem[]
    totalCount: number
  }
}

type VendorListSearchForSeo = {
  name?: string
  categories?: string[]
  location?: string
  distance?: number
  sortBy?: string
  myVendors?: boolean
  needsReview?: boolean
}

/** True when URL search filters narrow the catalog (not “my vendors” / review queues). */
function hasActiveVendorListFilters(search: VendorListSearchForSeo): boolean {
  if (search.name?.trim()) return true
  if (search.categories?.length) return true
  if (search.location?.trim()) return true
  if (typeof search.distance === 'number' && search.distance !== DEFAULT_VENDOR_LIST_DISTANCE) return true
  return Boolean(search.sortBy)
}

function buildVendorListBreadcrumbJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Vendor Market', item: absoluteUrlForPath('/vendor-market') },
      { '@type': 'ListItem', position: 3, name: 'Discover Vendors', item: absoluteUrlForPath(LIST_PATH) },
    ],
  }
}

function buildVendorListItemListJsonLd(input: {
  hasFilters: boolean
  items: VendorListSeoItem[]
  totalCount: number
}): Record<string, unknown> {
  const tr = i18n.t.bind(i18n)
  const base: Record<string, unknown> = {
    '@type': 'ItemList',
    numberOfItems: input.totalCount,
  }

  if (input.hasFilters) {
    base.name = tr('Filtered vendor search results')
  }

  if (input.items.length > 0) {
    base.itemListElement = input.items.map((row, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: row.name,
      item: absoluteUrlForPath(row.path),
      ...(row.imageUrl ? { image: row.imageUrl } : {}),
    }))
  }

  return base
}

export function vendorMarketListPageHeadFromLoaderData(
  loaderData: VendorListSeoLoaderData | null | undefined,
  search: VendorListSearchForSeo
) {
  const tr = i18n.t.bind(i18n)
  const vmDescription = tr('Browse vendors offering trades and services to the community.')
  const canonicalUrl = `${baseUrl()}${LIST_PATH}`
  const hasFilters = hasActiveVendorListFilters(search)

  const items = loaderData?.seoList.items ?? []
  const totalCount = loaderData?.seoList.totalCount ?? 0

  const searchResultsPage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    url: canonicalUrl,
    name: tr('Vendors'),
    description: vmDescription,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl()}${LIST_PATH}?name={query}`,
      },
      queryInput: 'required name=query',
    },
    mainEntity: buildVendorListItemListJsonLd({ hasFilters, items, totalCount }),
  }

  const jsonLd: Record<string, unknown>[] = [searchResultsPage, buildVendorListBreadcrumbJsonLd()]

  const ogImage = items[0]?.imageUrl

  return seo({
    title: `${tr('Vendors')} | ${env.VITE_APP_NAME}`,
    description: vmDescription,
    url: canonicalUrl,
    ...(ogImage ? { image: ogImage } : {}),
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd,
  })
}
